/**
 * 路径规划纯工具函数
 *
 * 职责：
 * 1. 坐标文本解析与校验（parseCoordinate）
 * 2. 坐标/里程/耗时的展示格式化
 *
 * 不依赖 React / store / BMapGL，可独立单测
 */
import type { RoutePlanLngLat } from './fetch';

export type ParseCoordinateResult =
  | { ok: true; point: RoutePlanLngLat }
  | { ok: false };

/** 有限小数：可选负号 + 整数位 + 可选小数位（拒绝 Infinity/NaN/多小数点等） */
const NUMBER_RE = /^-?\d+(\.\d+)?$/;

/**
 * 解析 "lng,lat" 坐标文本
 * 兼容中文逗号与空白 trim；经度 ∈ [-180,180]，纬度 ∈ [-90,90]
 */
export function parseCoordinate(text: string): ParseCoordinateResult {
  if (!text) return { ok: false };
  const parts = text
    .replace(/，/g, ',')
    .split(',')
    .map((part) => part.trim());
  if (parts.length !== 2) return { ok: false };
  const [lngText, latText] = parts;
  if (!NUMBER_RE.test(lngText) || !NUMBER_RE.test(latText)) {
    return { ok: false };
  }
  const lng = Number(lngText);
  const lat = Number(latText);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return { ok: false };
  if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return { ok: false };
  return { ok: true, point: { lng, lat } };
}

/** 坐标格式化：固定 6 位小数 "lng,lat" */
export function formatCoord(point: RoutePlanLngLat): string {
  return `${point.lng.toFixed(6)},${point.lat.toFixed(6)}`;
}

/** 里程格式化：<1000 → "xxx m"，否则 "x.xx km" */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(2)} km`;
}

/** 耗时格式化：秒 → "约 x 分钟"（不足 30s 按 1 分钟兜底，避免展示"约 0 分钟"） */
export function formatDuration(seconds: number): string {
  return `约 ${Math.max(1, Math.round(seconds / 60))} 分钟`;
}

/**
 * 路径规划业务视觉资产（路线样式 + 起终点 marker 图标）。
 * 属于业务资产，不放 map-core/styles.ts（map-core 保持无业务感知）。
 */

/** 路径规划路线样式（LineLayer strokeColor/strokeWeight；纹理铺贴宽度要够 12+ 箭头才可见） */
export const ROUTE_LINE_STYLE = {
  color: '#FF6A00',
  // 线宽：隔壁带箭头线为 7，本项目底图下取 8
  width: 8,
};

/** 路径规划起点 marker 图标（绿色图钉 + 白色"起"字，SVG data-URI） */
export const ROUTE_PLAN_START_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="#52c41a"/><text x="12" y="11" text-anchor="middle" dominant-baseline="central" font-size="13" font-weight="bold" fill="#fff">起</text></svg>'
)}`;

/** 路径规划终点 marker 图标（红色图钉 + 白色"终"字，SVG data-URI） */
export const ROUTE_PLAN_END_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="#f5222d"/><text x="12" y="11" text-anchor="middle" dominant-baseline="central" font-size="13" font-weight="bold" fill="#fff">终</text></svg>'
)}`;

/**
 * 路线纹理（带缓存）：16×64 竖向条纹，橙色打底 + 白色描边箭头（尾部在下、头部在上，
 * LineLayer 沿线方向重复铺贴即得方向箭头）。
 * 参数与 operation-platform-ui LogisticMap/utils/lineTextureFactory 完全对齐（隔壁已验证）。
 * node 环境无 canvas —— 仅在浏览器渲染路径调用（坑 10 同理，运行时才访问 document）。
 */
export const ROUTE_LINE_TEXTURE_WIDTH = 16;
export const ROUTE_LINE_TEXTURE_HEIGHT = 64;
const ROUTE_LINE_ARROW_RATIO = 0.25;
let routeLineTextureCache: string | null = null;

export const createRouteLineTexture = (): string => {
  if (routeLineTextureCache) return routeLineTextureCache;

  const canvas = document.createElement('canvas');
  canvas.width = ROUTE_LINE_TEXTURE_WIDTH;
  canvas.height = ROUTE_LINE_TEXTURE_HEIGHT;
  const ctx = canvas.getContext('2d')!;
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;

  // 1. 整张贴图底色 = 线色
  ctx.fillStyle = ROUTE_LINE_STYLE.color;
  ctx.fillRect(0, 0, w, h);

  // 2. 白色箭头外轮廓（三角形：底边两翼点 + 头部尖点）
  const arrowSize = Math.min(w, h * ROUTE_LINE_ARROW_RATIO);
  const arrowY = (h - arrowSize) / 2;
  const halfSize = arrowSize / 2;
  const wingW = halfSize * 0.85;
  const innerW = halfSize * 0.35;
  const tailY = arrowY + arrowSize * 0.25;
  const headY = arrowY + arrowSize * 0.92;
  const notchY = tailY + (headY - tailY) * 0.45;

  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.moveTo(cx - wingW, tailY);
  ctx.lineTo(cx, headY);
  ctx.lineTo(cx + wingW, tailY);
  ctx.closePath();
  ctx.fill();

  // 3. 用线色挖去内部小三角，形成描边箭头
  ctx.fillStyle = ROUTE_LINE_STYLE.color;
  ctx.beginPath();
  ctx.moveTo(cx - innerW, tailY);
  ctx.lineTo(cx, notchY);
  ctx.lineTo(cx + innerW, tailY);
  ctx.closePath();
  ctx.fill();

  routeLineTextureCache = canvas.toDataURL('image/png');
  return routeLineTextureCache;
};
